/* eslint-disable react/prop-types */
/* global globalThis */
import { useState, useRef } from "react";
import dayjs from 'dayjs';
import { useCertificateDownload, useStyles } from "sdk/custom-hooks";
import { useHistory } from "react-router-dom";
import { Box, Avatar, Typography, Grid, Chip, Card, CardContent, Paper, CircularProgress, IconButton, Popover } from "@material-ui/core";
import useTranslations from "sdk/custom-hooks/useTranslations";
import { useDispatch, useSelector } from "react-redux";
import { updateProfileImageDetails } from "sdk/actions/getUser";
import { ColorsConstant } from "../../constants/colorConstant";
import CameraAltOutlinedIcon from '@material-ui/icons/CameraAltOutlined';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import CallOutlinedIcon from '@material-ui/icons/CallOutlined';
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';
import CommonButton from "../../constants/CommonButton";
import PersonOutlinedIcon from '@material-ui/icons/PersonOutlined';
import ShareOutlinedIcon from '@material-ui/icons/ShareOutlined';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import TwitterIcon from '@material-ui/icons/Twitter';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import FacebookIcon from '@material-ui/icons/Facebook';
import { ReactComponent as PlatinumBadge } from "../../assets/images/platinumBadge.svg";
import { ReactComponent as Medal } from "../../assets/images/medal.svg";
import { ReactComponent as GoldBadge } from "../../assets/images/goldBadge.svg";
import { ReactComponent as BronzeBadge } from "../../assets/images/bronzeBadge.svg";
import { ReactComponent as PointsIcon } from "../../../src/assets/images/points.svg";
import { ReactComponent as CertificateIcon } from "../../../src/assets/images/DownloadImg.svg"
import Download from "../../assets/images/Download.png"
import { useTheme } from "../../constants/themeProvider";
import status from "sdk/constants/actionStatus.js";
import PropTypes from "prop-types";

const getUserField = (user, field) => user?.user?.[field] || user?.[field];

const getBadgeIcon = (badgeTitle, badgeUrl, classes) => {
  if (badgeUrl !== "default") {
    return <img className={classes.badgeSize} src={badgeUrl} alt={badgeTitle} />;
  }

  switch (badgeTitle) {
    case "Master":
      return <GoldBadge />;
    case "Knowledge Seeker":
      return <BronzeBadge />;
    case "Challenger":
      return <PlatinumBadge />;
    default:
      return <PlatinumBadge />;
  }
};

const getBadgeOrder = (badgeTitle) => {
  if (badgeTitle === "Knowledge Seeker") return 1;
  if (badgeTitle === "Challenger") return 2;
  if (badgeTitle === "Master") return 3;
  return 4;
};

const buildBadgeSections = (badges, classes) => {
  const groupedBadges = badges?.reduce((acc, badge) => {
    const category = badge?.category || "Others";
    if (!acc[category]) {
      acc[category] = [];
    }

    acc[category].push({
      name: badge.title,
      date: dayjs(badge.awardedDate).format("DD-MMM-YYYY"),
      icon: getBadgeIcon(badge.title, badge?.badgeUrl, classes),
      badgeUrl: badge?.badgeUrl,
      category: badge?.category,
      order: getBadgeOrder(badge.title),
    });
    return acc;
  }, {}) || {};

  return Object.keys(groupedBadges).map((category) => ({
    title: `${category.charAt(0).toUpperCase() + category.slice(1)} Badges`,
    badges: groupedBadges[category].sort(
      (firstBadge, secondBadge) => firstBadge.order - secondBadge.order
    ),
  }));
};

const getCertificateShareUrl = (platform, text, appUrl) => {
  switch (platform) {
    case "linkedin":
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(appUrl)}&title=${encodeURIComponent(text)}&summary=${encodeURIComponent(text)}`;
    case "twitter":
      return `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(appUrl)}`;
    case "whatsapp":
      return `https://api.whatsapp.com/send?text=${encodeURIComponent(text + "\n" + appUrl)}`;
    case "facebook":
      return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(appUrl)}&quote=${encodeURIComponent(text)}`;
    default:
      return "";
  }
};

const getBadgeShareUrl = (platform, text, badgeImageUrl, appUrl) => {
  switch (platform) {
    case "linkedin":
      return `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(text)}&url=${encodeURIComponent(badgeImageUrl)}`;
    case "whatsapp":
      return `https://api.whatsapp.com/send?text=${encodeURIComponent(text + "\n" + badgeImageUrl)}`;
    case "facebook":
      return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(badgeImageUrl || appUrl)}&quote=${encodeURIComponent(text)}`;
    default:
      return "";
  }
};

const validateProfileImage = (file, t) => {
  if (!file) {
    return { valid: false, error: null };
  }

  if (file.size > 1024 * 1024 * 5) {
    return {
      valid: false,
      error: t("Please upload profile image less than 5 MB"),
    };
  }

  const fileType = file.type;
  const validImageTypes = ["image/jpeg", "image/png", "image/jpg"];
  if (!validImageTypes.includes(fileType)) {
    return {
      valid: false,
      error: t("Invalid file type. Only jpeg, jpg and png images are allowed"),
    };
  }

  return { valid: true, error: null };
};

const getBadgeCaption = (badge) => `Excited to earn the '${badge?.name}' 🎉`;

const getCertificateShareText = (cert) => {
  const date = cert?.completedOn ? dayjs(cert.completedOn).format("DD-MMM-YYYY") : "";
  return `I just earned the "${cert?.courseName}" on ${date}! 🏆`;
};

const getTwitterShareUrl = async (text, badgeImageUrl, backendUrl) => {
  let imageCopied = false;
  if (badgeImageUrl) {
    try {
      const imageKey = badgeImageUrl.replace(/^https?:\/\/[^/]+\//, "");
      const proxyUrl = `${backendUrl}/badge/image/${imageKey}`;
      const response = await fetch(proxyUrl);
      const blob = await response.blob();
      const pngBlob = new Blob([blob], { type: "image/png" });
      await navigator.clipboard.write([new ClipboardItem({ "image/png": pngBlob })]);
      imageCopied = true;
    } catch (e) {
      console.error("Clipboard copy failed:", e);
    }
  }

  const tweetText = imageCopied
    ? `${text}\n\n📌 Badge image copied to clipboard! Press Ctrl+V / ⌘+V to paste.`
    : text;
  return `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
};

const shareCertificateOnPlatform = (platform, cert) => {
  const text = getCertificateShareText(cert);
  const appUrl = globalThis.location.origin;
  const shareUrl = getCertificateShareUrl(platform, text, appUrl);
  if (shareUrl) globalThis.open(shareUrl, "_blank", "noopener,noreferrer");
};

const shareBadgeOnPlatform = async (platform, badge) => {
  const text = getBadgeCaption(badge);
  const badgeImageUrl = badge?.badgeUrl || "";
  const appUrl = globalThis.location.origin;

  const shareUrl = platform === "twitter"
    ? await getTwitterShareUrl(text, badgeImageUrl, process.env.REACT_APP_BACKEND_URL)
    : getBadgeShareUrl(platform, text, badgeImageUrl, appUrl);

  if (shareUrl) globalThis.open(shareUrl, "_blank", "noopener,noreferrer");
};

const getPhoneDisplay = (countryCode, mobileNumber) => {
  if (!mobileNumber) {
    return "";
  }
  return countryCode ? `${countryCode} ${mobileNumber}` : mobileNumber;
};

const uploadProfileImage = async ({ file, fileDataUrl, dispatch, userId, setIsUploading }) => {
  try {
    setIsUploading(true);
    await dispatch(updateProfileImageDetails(userId, fileDataUrl, file.name));
  } catch (error) {
    console.error("Error updating profile image:", error.message);
  } finally {
    setIsUploading(false);
  }
};

const handleProfileImageSelection = ({ file, t, setErrorMessage, onValidFileSelected }) => {
  const validation = validateProfileImage(file, t);
  if (!validation.valid) {
    if (validation.error) {
      setErrorMessage(validation.error);
    }
    return;
  }

  setErrorMessage(null);

  const reader = new FileReader();
  reader.onload = async (event) => {
    await onValidFileSelected(file, event?.target?.result);
  };
  reader.readAsDataURL(file);
};

function BadgesSection({
  settingsData,
  isLoadingSettings,
  badgeSections,
  anySectionHasMoreThanOneBadge,
  anySectionHasMoreThanTwoBadges,
  classes,
  t,
  shareAnchorEl,
  handleShareClose,
  handleSocialShare,
  handleShareClick,
}) {
  const gamification = settingsData?.data?.[0]?.gamification;
  if (!(gamification?.enabled && gamification?.badgesEnabled)) {
    return null;
  }

  const shouldShowBadges = badgeSections?.length > 0 && !isLoadingSettings;

  return (
    <Box style={{ margin: "20px 0" }}>
      <Paper
        className="commonShadow"
        style={{
          padding: 20,
          height: "100%",
          border: `1px solid ${ColorsConstant.lightGreyBorder}`,
        }}
      >
        <Box style={{ marginBottom: "20px" }}>
          <Typography variant="inherit" className={classes.badgeTitle}>
            {t("My Badges")}
          </Typography>
        </Box>
        {shouldShowBadges && (
          <BadgesGrid
            badgeSections={badgeSections}
            anySectionHasMoreThanOneBadge={anySectionHasMoreThanOneBadge}
            anySectionHasMoreThanTwoBadges={anySectionHasMoreThanTwoBadges}
            classes={classes}
            handleShareClick={handleShareClick}
          />
        )}
        {shouldShowBadges === false && isLoadingSettings === false && (
          <Box>
            <Box display="flex" justifyContent="center" paddingBottom="10px"><Medal /></Box>
            <Box className={classes.noRecord}>{t("Badges earned will appear here. You haven’t earned any badges yet!")}</Box>
          </Box>
        )}
        <BadgesSharePopover
          classes={classes}
          shareAnchorEl={shareAnchorEl}
          handleShareClose={handleShareClose}
          handleSocialShare={handleSocialShare}
        />
      </Paper>
    </Box>
  );
}

function BadgesGrid({
  badgeSections,
  anySectionHasMoreThanOneBadge,
  anySectionHasMoreThanTwoBadges,
  classes,
  handleShareClick,
}) {
  return (
    <Grid container spacing={3}>
      {badgeSections.map((section) => (
        <Grid
          item
          xs={12}
          sm={anySectionHasMoreThanOneBadge ? 12 : 6}
          md={anySectionHasMoreThanTwoBadges ? 12 : 6}
          key={section.title}
        >
          <Box style={{ marginBottom: "10px" }}>
            <Typography variant="inherit" className={classes.badgeCategoryTitle}>
              {section.title}
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {section.badges.map((badge) => (
              <Grid
                item
                xs={12}
                sm={anySectionHasMoreThanOneBadge ? 4 : 12}
                md={anySectionHasMoreThanTwoBadges ? 3 : 6}
                key={`${badge.name}-${badge.date}`}
              >
                <Paper
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    minHeight: "70px",
                  }}
                >
                  <Box className={classes.badgeBox}>
                    <Box className={classes.badgeIcon}>
                      <Box>
                        {badge.badgeUrl ? (
                          <img src={badge.badgeUrl} alt={badge.name} style={{ width: 32, height: 32, objectFit: "contain" }} />
                        ) : (
                          badge.icon
                        )}
                      </Box>
                      <Typography variant="inherit" className={classes.badgeName}>
                        {badge.name}
                      </Typography>
                    </Box>
                    <Box className={classes.badgeBottomRow}>
                      <Typography variant="inherit" className={classes.badgeDate}>
                        {badge.date}
                      </Typography>
                      <IconButton
                        className={classes.shareIconBtn}
                        onClick={(e) => handleShareClick(e, badge)}
                        size="small"
                      >
                        <ShareOutlinedIcon style={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Grid>
      ))}
    </Grid>
  );
}

function BadgesSharePopover({ classes, shareAnchorEl, handleShareClose, handleSocialShare }) {
  return (
    <Popover
      open={Boolean(shareAnchorEl)}
      anchorEl={shareAnchorEl}
      onClose={handleShareClose}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      transformOrigin={{ vertical: "bottom", horizontal: "right" }}
    >
      <Box className={classes.sharePopover}>
        <IconButton className={classes.socialIcon} onClick={() => handleSocialShare("linkedin")} size="small">
          <LinkedInIcon style={{ fontSize: 20, color: "#0A66C2" }} />
        </IconButton>
        <IconButton className={classes.socialIcon} onClick={() => handleSocialShare("twitter")} size="small">
          <TwitterIcon style={{ fontSize: 20, color: "#1DA1F2" }} />
        </IconButton>
        <IconButton className={classes.socialIcon} onClick={() => handleSocialShare("whatsapp")} size="small">
          <WhatsAppIcon style={{ fontSize: 20, color: "#25D366" }} />
        </IconButton>
        <IconButton className={classes.socialIcon} onClick={() => handleSocialShare("facebook")} size="small">
          <FacebookIcon style={{ fontSize: 20, color: "#1877F2" }} />
        </IconButton>
      </Box>
    </Popover>
  );
}

BadgesSection.propTypes = {
  settingsData: PropTypes.object,
  isLoadingSettings: PropTypes.bool,
  badgeSections: PropTypes.array,
  anySectionHasMoreThanOneBadge: PropTypes.bool,
  anySectionHasMoreThanTwoBadges: PropTypes.bool,
  classes: PropTypes.object,
  t: PropTypes.func,
  shareAnchorEl: PropTypes.any,
  handleShareClose: PropTypes.func,
  handleSocialShare: PropTypes.func,
  handleShareClick: PropTypes.func,
};

BadgesGrid.propTypes = {
  badgeSections: PropTypes.array,
  anySectionHasMoreThanOneBadge: PropTypes.bool,
  anySectionHasMoreThanTwoBadges: PropTypes.bool,
  classes: PropTypes.object,
  handleShareClick: PropTypes.func,
};

BadgesSharePopover.propTypes = {
  classes: PropTypes.object,
  shareAnchorEl: PropTypes.any,
  handleShareClose: PropTypes.func,
  handleSocialShare: PropTypes.func,
};

function UserProfile() {
  const user = useSelector((state) => state.user);
  const userId = getUserField(user, "_id");
  const userName = getUserField(user, "name");
  const userEmail = getUserField(user, "email");
  const userMobile = getUserField(user, "mobileNumber");
  const userCity = getUserField(user, "city");
  const userRole = getUserField(user, "role");
  const userImage = getUserField(user, "image");
  const dispatch = useDispatch();
  const [shareAnchorEl, setShareAnchorEl] = useState(null);
  const [shareBadge, setShareBadge] = useState(null);
  const [certShareAnchorEl, setCertShareAnchorEl] = useState(null);
  const [shareCert, setShareCert] = useState(null);

  const handleShareClick = (event, badge) => {
    event.stopPropagation();
    setShareAnchorEl(event.currentTarget);
    setShareBadge(badge);
  };

  const handleShareClose = () => {
    setShareAnchorEl(null);
    setShareBadge(null);
  };

  const handleCertShareClose = () => {
    setCertShareAnchorEl(null);
    setShareCert(null);
  };

  const handleCertSocialShare = (platform) => {
    shareCertificateOnPlatform(platform, shareCert);
    handleCertShareClose();
  };

  const handleSocialShare = async (platform) => {
    await shareBadgeOnPlatform(platform, shareBadge);
    handleShareClose();
  };
  const t = useTranslations();
  const [errorMessage, setErrorMessage] = useState(null);
  const fileInputRef = useRef(null);
  const settings = useSelector((state) => state.settingsReducers);
  const isLoadingSettings = settings.status === status.PENDING;
  const settingsData = useSelector((state) => state.settingsReducers.settingsData?.data);
  const { handleDownloadCertificate } = useCertificateDownload({userName});
  const [isUploading, setIsUploading] = useState(false);
  const theme = useTheme();

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    handleProfileImageSelection({
      file,
      t,
      setErrorMessage,
      onValidFileSelected: async (selectedFile, fileDataUrl) => {
        await uploadProfileImage({
          file: selectedFile,
          fileDataUrl,
          dispatch,
          userId,
          setIsUploading,
        });
      },
    });
  };

  const styles = {
    noRecord: {
      justifyContent: 'center',
      display: 'flex',
      width: '100%'
    },
    fileUploadMsg: {
      color: "red",
      paddingTop: "14px",
      "@media (max-width: 599px)": {
        display:'none'
      }
    },
    fileUploadMsgMobile: {
      display:'none',
      "@media (max-width: 599px)": {
        display:'block',
        color: "red",
        paddingTop: "14px",
        textAlign:"center",
      }
    },
    mainBox: {
      padding: "20px 0px",
      paddingBottom: '80px',
      margin: '0px 16px 0px 16px',
      "@media (max-width: 767px)": {
        paddingTop: '10px',
      },
    },
    btnText: {
      color: ColorsConstant.white,
      fontSize: "12px",
      fontWeight: 600,
      padding: "5px 20px",
      textTransform: "Capitalize",
      borderRadius: "6px",
    },
    viewBtn: {
      borderRadius: "4px",
      padding: "0px",
      "&:hover": {
        backgroundColor: "#0c3450e0 !important",
      },
      "&:active": {
        boxShadow: "none",
      },
      "@media (max-width: 767px)": {
        marginLeft: 10,
      },
    },
    downloadIcon: {
      width: "16px",
      height: "16px",
      display: "block",
      marginLeft: "auto",
      marginRight: "16px",
      cursor: "pointer"
    },
    avatarWrapper: {
      position: "relative",
      width: 100,
      height: 100,
      "&:hover $cameraIcon": {
        opacity: 1,
      },
    },
    avatarWrapperMobile: {
      position: "relative",
      width: 120,
      height: 120,
      marginBottom: 16,
      cursor: "pointer",
    },
    image: {
      width: 100,
      height: 100,
      cursor: "pointer",
      border: '1px solid #4a4a4a24',
      "@media (max-width: 599px)": {
        width: 150,
        height: 150,
      }
    },
    avatarLoader: {
      position: "absolute",
      top: "0%",
      left: "0%",
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: "rgba(255, 255, 255, 0.5)",
      borderRadius: "50%",
      "@media (max-width: 599px)": {
        top: "10%",
        left: "10%",
      }
    },
    cameraIcon: {
      position: "absolute",
      bottom: "40%",
      right: "35%",
      backgroundColor: "#fff",
      borderRadius: "50%",
      padding: 4,
      opacity: 0,
      transition: "opacity 0.3s",
    },
    cameraIconMobile: {
      position: "absolute",
      bottom: "-28%",
      right: "-6%",
      transform: "translate(-50%, -50%)",
      backgroundColor: "#fff",
      borderRadius: "50%",
      padding: 6,
      boxShadow: "0 0 4px rgba(0,0,0,0.2)",
      zIndex: 2,
      fontSize: 24,
      color: "#555",
    },
    userName : {
      fontSize: '18px',
      fontWeight: 500, 
      marginBottom: "10px"
    },
    userDetails : {
      fontSize: '16px', 
      fontWeight: 400,
    },
    userLevel : {
      fontSize: '12px', 
      fontWeight: 600, 
      color: "#0A2A40", 
      marginRight: "10px"
    },
    labelChip: {
      backgroundColor: '#E0F2F1',
      padding: '0px 3px'
    },
    coinChip: {
      border: "2px solid #FFCC29", 
      color:"#FFCC29", 
      backgroundColor:"#fff",
      fontWeight: '700',
      '& span': {
        paddingTop: '3px'
      }
    },
    badgeTitle: {
      fontSize: "16px", 
      fontWeight: 600, 
      color: "#0C3450"
    },
    badgeCategoryTitle: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#202020',
    },
    badgeBox: {
      border:"1px solid #E0E0E0", 
      borderRadius:"6px", 
      padding:"16px",
      minHeight: '105px',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px'
    },
    badgeIcon: {
      display: 'flex', 
      alignItems: 'center',
      gap: '8px'
    },
    badgeName: {
      fontSize: '14px',
      fontWeight: 500,
      color: ColorsConstant.descriptionColor
    },
    badgeDate: {
      fontSize: '12px',
      fontWeight: 400,
      color: ColorsConstant.descriptionColor
    },
    badgeBottomRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    shareIconBtn: {
      padding: 4,
      color: '#9E9E9E',
      '&:hover': {
        color: '#0C3450',
        backgroundColor: 'transparent',
      },
    },
    sharePopover: {
      display: 'flex',
      gap: '4px',
      padding: '8px 12px',
    },
    socialIcon: {
      padding: 6,
      '&:hover': {
        backgroundColor: '#f5f5f5',
      },
    },
    certificateBox: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "16px",
    },
    certificateGrid: {
      display: 'flex', 
      alignItems: 'center',
      padding:"5px 0px"
    },
    courseName: {
      fontSize: '14px', 
      fontWeight: 400, 
      color: '#000000B2', 
      marginLeft: "16px"
    },
    certificateButton: {
      display: 'flex', 
      justifyContent: 'flex-end', 
      marginTop: "20px"
    },
    rankingBorder: {
      borderBottom: '1px solid #E5E7EB', 
      padding: '10px 0'
    },
    rankTitle: {
      fontSize: '12px', 
      fontWeight: 600, 
      color: ColorsConstant.descriptionColor,
      textTransform: 'uppercase', 
    },
    rankCourse: {
      fontSize: '14px', 
      fontWeight: 400, 
      color: "#111928",
      paddingRight: '10px', 
      display: 'block',
    },
    leaderboardRank: {
      cursor: "pointer",
      fontWeight: 500,
      textDecoration: "underline",
    },
    rankData: {
      fontSize: '14px', 
      fontWeight: 400, 
      color: ColorsConstant.descriptionColor
    },
    webContainer: {
      display: 'flex',
      alignItems: "center",
      justifyContent: "space-between",
      gap: '20px',
      "@media (max-width: 599px)": {
        display: 'none',
      }
    },
    rootContainer: {
      display: 'none',
      "@media (max-width: 599px)": {
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'center',
        alignItems: "center",
        justifyContent: "center", 
        gap: "16px",
      },
    },
    userInfoContainer: {
      "@media (max-width: 599px)": {
        alignItems: 'center',
      },
    },
    userDetailsRow: {
      "@media (max-width: 599px)": {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: '10px',
      },
    },
    iconsAlign: {
      display: 'flex'
    },
    badgeSize: {
      height: '36px',
      width: '36px',
      display: 'flex',
    }
  };

  const classes = useStyles(styles)();
  const history = useHistory();

  const badgeSections = buildBadgeSections(user?.badges, classes);

  const anySectionHasMoreThanOneBadge = badgeSections.some(
    (section) => section.badges.length > 1
  );

  const anySectionHasMoreThanTwoBadges = badgeSections.some(
    (section) => section.badges.length > 2
  );

  const userCountryCode = getUserField(user, "countryCode");

  const userDetails = [
    { key: "email", icon: <MailOutlineIcon fontSize="small" />, value: userEmail },
    { key: "mobile", icon: <CallOutlinedIcon fontSize="small" />, value: getPhoneDisplay(userCountryCode, userMobile) },
    { key: "city", icon: <LocationOnOutlinedIcon fontSize="small" />, value: userCity },
    { key: "role", icon: <PersonOutlinedIcon fontSize="small" />, value: userRole },
  ];

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  return (
      <Box className={classes.mainBox}>
        {/* Profile */}
        <Paper
          className="commonShadow"
          style={{
            padding: 20,
            height: "100%",
            border: `1px solid ${ColorsConstant.lightGreyBorder}`,
          }}
        >
          {/* Web */}
          <Grid container spacing={2} className={classes.webContainer}>
            {/* Left: Avatar */}
            <Grid item>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleImageChange}
              />
              <Box className={classes.avatarWrapper} onClick={triggerFileInput}>
                <Avatar src={userImage} className={classes.image} style={{backgroundColor: !userImage ? theme?.primaryColor: undefined}}/>
                <CameraAltOutlinedIcon className={classes.cameraIcon} />

                {isUploading && (
                  <Box className={classes.avatarLoader}>
                    <CircularProgress size={22} />
                  </Box>
                )}
              </Box>
            </Grid>
            {/* Center: User Info */}
            <Grid item xs>
              <Grid container alignItems="center" style={{padding: '15px 0px'}}>
                <Grid item>
                  <Typography variant="inherit" className={classes.userName}>
                    {userName}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container spacing={4} alignItems="center" style={{gap: '20px'}}>
                {userDetails.map((detail) => (
                  <Grid item key={detail.key}>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid className={classes.iconsAlign} item>{detail.icon}</Grid>
                      <Grid item>
                        <Typography variant="inherit" className={classes.userDetails}>
                          {detail.value}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
              {/* Level and Points */}
              <Grid container alignItems="center" style={{margin: '15px 0px', gap: '20px'}}>
                {settingsData?.data?.[0]?.gamification?.enabled && settingsData?.data?.[0]?.gamification?.levelsEnabled && <Grid item>
                  <Typography variant="inherit" className={classes.userLevel}>
                    {t('LEVEL')}
                  </Typography>
                  <Chip
                    label={user?.level || "N/A"}
                    size="small"
                    className={classes.labelChip}
                  />
                </Grid>}
                {settingsData?.data?.[0]?.gamification?.enabled && settingsData?.data?.[0]?.gamification?.pointsEnabled && <Grid item>
                  <Typography variant="inherit" className={classes.userLevel}>
                    {t('EARNED POINTS')}
                  </Typography>
                  <Chip
                    icon={<PointsIcon style={{ width: 16, height: 16 }} />}
                    label={user?.earnedPoints || "N/A"}
                    size="small"
                    className={classes.coinChip}
                  />
                </Grid>}
              </Grid>
            </Grid>
          </Grid>
          {errorMessage && (
              <Box className={classes.fileUploadMsg}>{errorMessage}</Box>
            )}
          {/* Mobile */}
          <div className={classes.rootContainer}>
            {/* Avatar Section */}
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleImageChange}
            />
            <Box className={classes.avatarWrapperMobile} onClick={triggerFileInput}>
              <Avatar src={userImage} className={classes.image} style={{backgroundColor: !userImage ? theme?.primaryColor : undefined}}/>
              <CameraAltOutlinedIcon className={classes.cameraIconMobile} />
              {isUploading && (
                <Box className={classes.avatarLoader}>
                  <CircularProgress size={22} />
                </Box>
              )}
            </Box>
            {errorMessage && (
              <Box className={classes.fileUploadMsgMobile}>{errorMessage}</Box>
            )}
            {/* User Info Section */}
            <div className={classes.userInfoContainer}>
              <Typography variant="inherit" className={classes.userName}>
                {userName}
              </Typography>

              {userDetails.map((detail) => (
                <div key={detail.key} className={classes.userDetailsRow}>
                  <span>
                    {detail.icon}
                  </span>
                  <Typography
                    variant="inherit"
                    className={classes.userDetails}
                    style={{ marginLeft: "10px" }}
                  >
                    {detail.value}
                  </Typography>
                </div>
              ))}
              {/* Level and Points */}
              {
                settingsData?.data?.[0]?.gamification?.enabled && settingsData?.data?.[0]?.gamification?.levelsEnabled && user?.level ? <div className={classes.userDetailsRow}>
                  <Typography variant="inherit" className={classes.userLevel}>
                    {t('LEVEL')}
                  </Typography>
                  <Chip label={user?.level} size="small" className={classes.labelChip} />
                </div> : <></>
              }
              {
                settingsData?.data?.[0]?.gamification?.enabled && settingsData?.data?.[0]?.gamification?.pointsEnabled && user?.earnedPoints ? <div className={classes.userDetailsRow}>
                  <Typography variant="inherit" className={classes.userLevel}>
                    {t('EARNED POINTS')}
                  </Typography>
                  <Chip
                    icon={<PointsIcon style={{ width: 16, height: 16 }} />}
                    label={user?.earnedPoints}
                    size="small"
                    className={classes.coinChip}
                  />
                </div> : <></>
              }
            </div>
          </div>
        </Paper>

        <BadgesSection
          settingsData={settingsData}
          isLoadingSettings={isLoadingSettings}
          badgeSections={badgeSections}
          anySectionHasMoreThanOneBadge={anySectionHasMoreThanOneBadge}
          anySectionHasMoreThanTwoBadges={anySectionHasMoreThanTwoBadges}
          classes={classes}
          t={t}
          shareAnchorEl={shareAnchorEl}
          handleShareClose={handleShareClose}
          handleSocialShare={handleSocialShare}
          handleShareClick={handleShareClick}
        />

        <Grid container spacing={3}>
          {/* Certifications */}
          <Grid item xs={12} md={6}>
            <Paper
              className="commonShadow"
              style={{
                padding: "16px",
                height: "100%",
                border: `1px solid ${ColorsConstant.lightGreyBorder}`,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Card style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                <CardContent style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                  <Box style={{ flexGrow: 1 }}>
                    <Box className={classes.certificateBox}>
                      <Typography variant="inherit" className={classes.badgeTitle}>
                        {t('My Certifications')}
                      </Typography>
                    </Box>

                    <Grid container spacing={2}>
                      {user?.certificates?.length > 0 ? user?.certificates?.slice(0, 4).map((cert) => (
                        <Grid item xs={12} key={`${cert?.courseName || "certificate"}-${cert?.completedOn || "na"}`}>
                          <Box className={classes.certificateGrid}>
                            <Box className={classes.iconsAlign}>
                              <CertificateIcon alt="Certificate" />
                            </Box>
                            <Typography variant="inherit" className={classes.courseName}>
                              {cert?.courseName}
                            </Typography>
                            <Box
                              component="img"
                              className={classes.downloadIcon}
                              src={Download}
                              alt="Download Icon"
                              onClick={() => handleDownloadCertificate(cert?.courseName, cert?.completedOn)}
                            />
                          </Box>
                        </Grid>
                      )) : <Box className={classes.noRecord} justifyContent="left !important" padding="7px">{t("Complete courses successfully to earn certificates. You can download your earned certificates from here.")}</Box>}
                    </Grid>
                  </Box>

                  <Popover
                    open={Boolean(certShareAnchorEl)}
                    anchorEl={certShareAnchorEl}
                    onClose={handleCertShareClose}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  >
                    <Box className={classes.sharePopover}>
                      <IconButton className={classes.socialIcon} onClick={() => handleCertSocialShare('linkedin')} size="small">
                        <LinkedInIcon style={{ fontSize: 20, color: '#0A66C2' }} />
                      </IconButton>
                      <IconButton className={classes.socialIcon} onClick={() => handleCertSocialShare('twitter')} size="small">
                        <TwitterIcon style={{ fontSize: 20, color: '#1DA1F2' }} />
                      </IconButton>
                      <IconButton className={classes.socialIcon} onClick={() => handleCertSocialShare('whatsapp')} size="small">
                        <WhatsAppIcon style={{ fontSize: 20, color: '#25D366' }} />
                      </IconButton>
                      <IconButton className={classes.socialIcon} onClick={() => handleCertSocialShare('facebook')} size="small">
                        <FacebookIcon style={{ fontSize: 20, color: '#1877F2' }} />
                      </IconButton>
                    </Box>
                  </Popover>

                  {user?.certificates?.length > 0 && <Box className={classes.certificateButton}>
                    <CommonButton
                      onClick={() => history.push("/certificate-list")}
                      title={t('View All')}
                      classStyleHeading={classes.viewBtn}
                      classStyleText={classes.btnText}
                    />
                  </Box>}
                </CardContent>
              </Card>
            </Paper>
          </Grid>

          {/* Rankings */}
          {
            settingsData?.data?.[0]?.gamification?.enabled && !isLoadingSettings ? (<Grid item xs={12} md={6}>
              <Paper
                className="commonShadow"
                style={{
                  padding: 16,
                  height: "100%",
                  border: `1px solid ${ColorsConstant.lightGreyBorder}`,
                }}
              >
                <Card>
                  <CardContent>
                    <Box className={classes.certificateBox}>
                      <Typography variant="inherit" className={classes.badgeTitle}>
                        {t('My Ranking')}
                      </Typography>
                    </Box>
                    {user?.topRankings?.length > 0 ? <Grid container className={classes.rankingBorder}>
                      <Grid item xs={6}>
                        <Typography variant="inherit" className={classes.rankTitle}>
                          {t('COURSE NAME')}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="inherit" className={classes.rankTitle}>
                          {t('Points')}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="inherit" className={classes.rankTitle}>
                          {t('rank')}
                        </Typography>
                      </Grid>
                    </Grid> : 
                    !isLoadingSettings && (
                      <Box className={classes.noRecord} justifyContent="left !important">{t("Earn points, badges, or rewards to secure your place on the leaderboard.")}</Box>
                    )}
                    {user?.topRankings?.map((rank) => (
                      <Grid container key={`${rank?.courseId || rank?.courseName || "rank"}-${rank?.rank || "na"}`} alignItems="center" className={classes.rankingBorder}>
                        <Grid item xs={6}>
                          <Typography
                            variant="inherit"
                            className={`${classes.rankCourse} ${rank?.courseId &&
                                settingsData?.data?.[0]?.gamification?.enabled &&
                                settingsData?.data?.[0]?.gamification?.leaderboardEnabled
                                ? classes.leaderboardRank
                                : ''
                              }`}
                            onClick={() => {
                              if (rank?.courseId && settingsData?.data?.[0]?.gamification?.enabled && settingsData?.data?.[0]?.gamification?.leaderboardEnabled) {
                                history.push(`course/leaderboard/${rank?.courseId}`, { params: { courseId: rank?.courseId, courseName: rank?.courseName } });
                              }
                            }}
                          >
                            {rank.courseName}
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="inherit " className={classes.rankData}>
                            {rank.totalPoints}
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="inherit" className={classes.rankData}>
                            {rank.rank}
                          </Typography>
                        </Grid>
                      </Grid>
                    ))}
                    {
                      settingsData?.data?.[0]?.gamification?.leaderboardEnabled && user?.topRankings?.length > 0 ? <Box className={classes.certificateButton}>
                        <CommonButton
                          title={t('View All')}
                          classStyleHeading={classes.viewBtn}
                          classStyleText={classes.btnText}
                          onClick={() => {
                            const firstRankId = user?.topRankings?.[0]?.courseId;
                            const firstCourseName = user?.topRankings?.[0]?.courseName
                            if (firstRankId) {
                              history.push(`course/leaderboard/${firstRankId}`, { params: { courseId: firstRankId, courseName: firstCourseName } });
                            }
                          }}
                        />
                      </Box> : <></>
                    }
                  </CardContent>
                </Card>
              </Paper>
            </Grid>) : <></>
          }
        </Grid>
      </Box>
  );
}

export default UserProfile;
